import type { ClientLogEntry } from '~/interfaces/ClientLogEntry'
import type { ClientLogRelay } from '~/interfaces/ClientLogRelay'
import type { ClientLogRelayOptions } from '~/interfaces/ClientLogRelayOptions'
import { boundLogContext } from '~/utils/boundLogContext'
import { createClientLogDroppedEntry } from '~/utils/createClientLogDroppedEntry'
import { createTokenBucket } from '~/utils/createTokenBucket'
import { isValidClientLogEvent } from '~/utils/isValidClientLogEvent'

const FLUSH_SIZE = 20
const FLUSH_DELAY_MS = 2000
const BUDGET_WINDOW_MS = 60_000
const ENTRIES_PER_MINUTE = 29
const SUMMARIES_PER_MINUTE = 1
const REQUESTS_PER_MINUTE = 30
const REQUEST_BURST = 2
const MAX_MESSAGE_LENGTH = 2000
const MAX_ENTRIES_PER_REQUEST = 50
const MAX_REQUEST_BYTES = 256 * 1024
// The browser's 64 KiB keepalive limit includes every unfinished request body.
const KEEPALIVE_MAX_BYTES = 60 * 1024
const encoder = new TextEncoder()
const EMPTY_BODY_BYTES = encoder.encode(JSON.stringify({ entries: [] })).length

interface PendingEntry {
  readonly entry: ClientLogEntry
  readonly summary: boolean
  count: number
}

const normalizeContext = (
  context: NonNullable<ClientLogEntry['context']>
): NonNullable<ClientLogEntry['context']> =>
  Object.fromEntries(
    Object.entries(boundLogContext(context).context).map(([key, value]) => [
      key.toWellFormed(),
      typeof value === 'string' ? value.toWellFormed() : value
    ])
  )

const toWireEntry = ({ entry, count }: PendingEntry): ClientLogEntry =>
  count > 1 ? { ...entry, context: normalizeContext({ ...entry.context, count }) } : entry

const isSameEntry = (first: ClientLogEntry, second: ClientLogEntry): boolean =>
  first.event === second.event &&
  first.level === second.level &&
  first.message === second.message &&
  JSON.stringify(first.context) === JSON.stringify(second.context)

export function createClientLogRelay(options: ClientLogRelayOptions): ClientLogRelay {
  const entryBudget = createTokenBucket(ENTRIES_PER_MINUTE, BUDGET_WINDOW_MS)
  const summaryBudget = createTokenBucket(SUMMARIES_PER_MINUTE, BUDGET_WINDOW_MS)
  const sendEntryBudget = createTokenBucket(ENTRIES_PER_MINUTE, BUDGET_WINDOW_MS)
  const sendSummaryBudget = createTokenBucket(SUMMARIES_PER_MINUTE, BUDGET_WINDOW_MS)
  const requestBudget = createTokenBucket(
    REQUEST_BURST,
    (BUDGET_WINDOW_MS * REQUEST_BURST) / (REQUESTS_PER_MINUTE - REQUEST_BURST)
  )
  let pending: PendingEntry[] = []
  let dropped = 0
  let normalRequests = 0
  let outstandingKeepaliveBytes = 0
  let sessionEstablished = false
  let exitInFlight = false
  let timer: ReturnType<typeof setTimeout> | undefined

  const ensureTimer = (): void => {
    timer ??= setTimeout(() => {
      flush()
    }, FLUSH_DELAY_MS)
  }

  const takeSummary = (): void => {
    const hasRoom = pending.length < MAX_ENTRIES_PER_REQUEST
    if (dropped === 0 || !hasRoom || summaryBudget.take(1) === 0) return
    pending.unshift({ entry: createClientLogDroppedEntry(dropped), summary: true, count: 1 })
    dropped = 0
  }

  const takeBatch = (maxBytes: number, allowGaps: boolean): ClientLogEntry[] => {
    const entries: ClientLogEntry[] = []
    const remaining: PendingEntry[] = []
    let used = EMPTY_BODY_BYTES
    let canFitLater = true
    for (const candidate of pending) {
      const entry = toWireEntry(candidate)
      const size = encoder.encode(JSON.stringify(entry)).length + (entries.length > 0 ? 1 : 0)
      const budget = candidate.summary ? sendSummaryBudget : sendEntryBudget
      const fitsSize = used + size <= maxBytes
      const fits =
        entries.length < MAX_ENTRIES_PER_REQUEST &&
        fitsSize &&
        canFitLater &&
        budget.available() >= 1
      if (fits) {
        budget.take(1)
        entries.push(entry)
        used += size
        continue
      }
      remaining.push(candidate)
      if (!allowGaps && !fitsSize) canFitLater = false
    }
    pending = remaining
    return entries
  }

  const dispatch = (entries: readonly ClientLogEntry[], keepalive: boolean): void => {
    requestBudget.take(1)
    const bytes = keepalive ? encoder.encode(JSON.stringify({ entries })).length : 0
    outstandingKeepaliveBytes += bytes
    if (keepalive) exitInFlight = true
    else normalRequests += 1

    let sent: Promise<void>
    try {
      sent = options.send(entries, keepalive)
    } catch (error) {
      sent = Promise.reject(
        error instanceof Error ? error : new Error('Client log relay send failed')
      )
    }
    sent
      .then(() => {
        sessionEstablished = true
      })
      .catch(() => {
        // Logging failures cannot replace the failure being reported.
      })
      .finally(() => {
        outstandingKeepaliveBytes -= bytes
        if (keepalive) exitInFlight = false
        else normalRequests -= 1
        if (pending.length > 0 || dropped > 0) flush()
      })
  }

  const flush = (keepalive = false): void => {
    clearTimeout(timer)
    timer = undefined
    takeSummary()
    const canStartRequest = requestBudget.available() >= 1
    if (!canStartRequest) {
      if (pending.length > 0 || dropped > 0) ensureTimer()
      return
    }

    if (keepalive) {
      if (!exitInFlight) {
        const entries = takeBatch(KEEPALIVE_MAX_BYTES - outstandingKeepaliveBytes, true)
        if (entries.length > 0) dispatch(entries, true)
      }
      if (pending.length > 0 || dropped > 0) ensureTimer()
      return
    }

    const canSend = normalRequests === 0 && (sessionEstablished || !exitInFlight)
    if (!canSend) return
    const entries = takeBatch(MAX_REQUEST_BYTES, false)
    if (entries.length > 0) dispatch(entries, false)
    if (pending.length > 0 || dropped > 0) ensureTimer()
  }

  const push = (received: ClientLogEntry): void => {
    if (!isValidClientLogEvent(received.event)) {
      dropped += 1
      ensureTimer()
      return
    }

    const entry: ClientLogEntry = {
      event: received.event,
      level: received.level,
      message: received.message.slice(0, MAX_MESSAGE_LENGTH).toWellFormed(),
      ...(received.context !== undefined ? { context: normalizeContext(received.context) } : {})
    }
    const last = pending.at(-1)
    if (last !== undefined && isSameEntry(last.entry, entry)) {
      last.count += 1
      return
    }

    const hasRoom = pending.length < MAX_ENTRIES_PER_REQUEST
    if (!hasRoom || entryBudget.take(1) === 0) {
      dropped += 1
      ensureTimer()
      return
    }
    pending.push({ entry, summary: false, count: 1 })
    if (pending.length >= FLUSH_SIZE) {
      flush()
      return
    }
    ensureTimer()
  }

  return { push, flush }
}
